import { useCallback, useEffect, useRef, useState } from 'react';
import {
  rectFromPoints, esZonaValida, normalizeRect, anclasDeZona,
  type Rect, type NormalizedRect, type CandidatoZona,
} from './regionAnchor.js';

// ── Rayar una zona sobre la vista previa ─────────────────────────────────────
//
// Para cuando lo que se quiere marcar no es un elemento sino un área: el espacio
// entre dos bloques, una franja que se ve apretada, dos cosas que no se alinean.
//
// El rectángulo que se dibuja NO es el ancla. El ancla son los data-evo-id de lo
// que la zona cubre, que sobreviven a que la página se regenere; el rectángulo se
// guarda aparte para recortar la captura y para volver a dibujar la marca.

/** Marca del arrastre. Vive fuera de <body>, como el resto de los overlays. */
const DRAW_ID = '__evo-region-draw';

function overlay(doc: Document): HTMLElement {
  let el = doc.getElementById(DRAW_ID);
  if (!el) {
    el = doc.createElement('div');
    el.id = DRAW_ID;
    // Los colores van literales y no con var(): este div se inyecta en el
    // documento del iframe, que carga el CSS del template y no tokens.css.
    // Copiados de packages/tokens/tokens.css — ver overlays.ts.
    el.style.cssText = [
      'position:absolute',
      'pointer-events:none',
      'z-index:2147483646',
      'border:1px dashed #00FAC8',
      'background:rgba(0, 250, 200, 0.12)',
      'border-radius:2px',
    ].join(';');
    doc.documentElement.appendChild(el);
  }
  return el;
}

function quitarOverlay(doc: Document | null | undefined): void {
  doc?.getElementById(DRAW_ID)?.remove();
}

export interface RegionResult {
  /** Rectángulo normalizado (0..1) contra el tamaño total de la página. */
  rect: NormalizedRect;
  /** Ids de lo que la zona cubre. Esto es el ancla. */
  targetIds: string[];
  /** Ancho con el que se estaba mirando, para recortar la captura igual. */
  viewportWidth: number;
}

export interface UseRegionDrawOpts {
  enabled: boolean;
  /**
   * El <iframe>, no su documento.
   *
   * Registrar los listeners una sola vez contra el documento no alcanza: si el
   * iframe todavía no cargó cuando se entra en modo comentar, no hay documento
   * al que engancharse y el efecto no se vuelve a correr — el arrastre no hacía
   * nada. Y cada recarga reemplaza el documento, así que hay que re-enganchar.
   * Es el mismo patrón que usa la selección.
   */
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  onRegion: (r: RegionResult) => void;
}

export function useRegionDraw({ enabled, iframeRef, onRegion }: UseRegionDrawOpts): { dibujando: boolean } {
  const [dibujando, setDibujando] = useState(false);
  const inicio = useRef<{ x: number; y: number } | null>(null);
  // Los handlers se registran una vez; leer de refs evita re-registrar en cada
  // render y perder un arrastre a la mitad.
  const enabledRef = useRef(enabled);
  const onRegionRef = useRef(onRegion);
  useEffect(() => { enabledRef.current = enabled; });
  useEffect(() => { onRegionRef.current = onRegion; });

  /** Coordenadas dentro del documento de la página, no de la ventana. */
  const punto = useCallback((e: MouseEvent, doc: Document) => ({
    x: e.clientX + (doc.defaultView?.scrollX ?? 0),
    y: e.clientY + (doc.defaultView?.scrollY ?? 0),
  }), []);

  const pintar = useCallback((doc: Document, r: Rect) => {
    const el = overlay(doc);
    el.style.left = `${String(r.left)}px`;
    el.style.top = `${String(r.top)}px`;
    el.style.width = `${String(r.width)}px`;
    el.style.height = `${String(r.height)}px`;
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let limpiar: (() => void) | null = null;

    const enganchar = (): void => {
      limpiar?.();
      const doc = iframe.contentWindow?.document;
      const win = iframe.contentWindow;
      if (!doc || !win) return;
      if (!enabled) { quitarOverlay(doc); return; }

      const onDown = (e: MouseEvent): void => {
        if (!enabledRef.current) return;
        inicio.current = punto(e, doc);
        setDibujando(true);
        pintar(doc, { left: inicio.current.x, top: inicio.current.y, width: 0, height: 0 });
        // Sin esto el navegador arranca a seleccionar el texto de la página
        // mientras se arrastra, y queda todo pintado de azul.
        e.preventDefault();
      };

      const onMove = (e: MouseEvent): void => {
        if (!inicio.current) return;
        pintar(doc, rectFromPoints(inicio.current, punto(e, doc)));
      };

      const onUp = (e: MouseEvent): void => {
        const desde = inicio.current;
        inicio.current = null;
        setDibujando(false);
        quitarOverlay(doc);
        if (!desde) return;

        const rect = rectFromPoints(desde, punto(e, doc));
        // Un clic con pulso no es una zona: se ignora en vez de crear un
        // comentario anclado a nada.
        if (!esZonaValida(rect)) return;

        const pagina = {
          width: doc.documentElement.scrollWidth,
          height: doc.documentElement.scrollHeight,
        };
        const normalizado = normalizeRect(rect, pagina.width, pagina.height);
        if (!normalizado) return;

        const scrollX = win.scrollX;
        const scrollY = win.scrollY;
        const candidatos: CandidatoZona[] = [];
        for (const el of doc.querySelectorAll('[data-evo-id]')) {
          const id = el.getAttribute('data-evo-id');
          if (!id) continue;
          const b = el.getBoundingClientRect();
          if (b.width <= 0 || b.height <= 0) continue;
          let depth = 0;
          for (let p = el.parentElement; p; p = p.parentElement) depth++;
          candidatos.push({
            targetId: id,
            rect: { left: b.left + scrollX, top: b.top + scrollY, width: b.width, height: b.height },
            depth,
          });
        }

        const targetIds = anclasDeZona(candidatos, rect);
        if (targetIds.length === 0) return;
        onRegionRef.current({ rect: normalizado, targetIds, viewportWidth: pagina.width });
      };

      win.addEventListener('mousedown', onDown, true);
      win.addEventListener('mousemove', onMove, true);
      win.addEventListener('mouseup', onUp, true);
      limpiar = () => {
        win.removeEventListener('mousedown', onDown, true);
        win.removeEventListener('mousemove', onMove, true);
        win.removeEventListener('mouseup', onUp, true);
        quitarOverlay(doc);
      };
    };

    iframe.addEventListener('load', enganchar);
    // El documento puede estar ya cargado cuando se entra en modo comentar.
    if (iframe.contentWindow?.document.readyState === 'complete') enganchar();
    return () => {
      iframe.removeEventListener('load', enganchar);
      limpiar?.();
    };
  }, [enabled, iframeRef, punto, pintar]);

  return { dibujando };
}
